/**
 * AngularJS module providing debounce and throttle.
 * https://github.com/Brakebein/ng-debounce-throttle
 *
 * Code and documentation are partly adopted from
 * * https://github.com/shahata/angular-debounce Copyright (c) 2013 Steve
 * * https://github.com/BaggersIO/angular.throttle
 *
 * @module qpDebounceThrottle
 * @name qpDebounceThrottle
 * @ngdoc module
 */

(function () {

	'use strict';

	function $debounce($rootScope, $timeout) {

		/**
		 * Service that creates and returns a new debounced version of the passed function, which will postpone its execution until after **delay** milliseconds have elapsed since the last time it was invoked. Useful for implementing behavior that should only happen *after* the input has stopped arriving. For example: recalculating a layout after the window has stopped being resized.
		 * @ngdoc factory
		 * @name $debounce
		 * @module qpDebounceThrottle
		 * @param callback {function} The function we want to debounce.
		 * @param delay {number} Number of  milliseconds to wait before invoking the debounced function.
		 * @param [leading=false] {boolean} If true, the function is triggered on the leading instead of the trailing edge of the **delay** interval. Useful for circumstances like preventing accidental double-clicks on a "submit" button from firing a second time. Default: `false`
		 * 		   如果为真，则在起始处触发该函数，而不是在**delay** interval的后沿。对于防止意外双击“submit”按钮而导致第二次触发的情况非常有用。默认值:“假”
		 * @param [invokeApply=true] {boolean} If true, an angular digest cycle is triggered (see $timeout service for more details). Default: `true`
		 * 			如果为真，则触发angular摘要循环(更多细节见$timeout service)。默认值:“真正的”
		 * @return A debounced version of the passed function. Any arguments passed to this function will be also passed. The returned function also has a `cancel()` method, which can be used in case you want to reset the current debounce state. This will prevent the function from being triggered even after **delay** milliseconds have passed from last input. In case **leading** is `true`, the next user input will trigger the debounce.
		 */
		return function (callback, delay, leading, invokeApply) {
			/**
			 * context:执行环境
			 * args：传入callback函数中的参数
			 * timeout：定时器
			 * result ：callback函数执行的返回参数
			 */
			var context, args, timeout, result;

			var later = function () {
				timeout = null;
				if (leading !== true) {
					if (invokeApply !== false) $rootScope.$applyAsync();
					result = callback.apply(context, args);
				}
			};

			function debounce() {

				context = this;
				args = arguments;

				var callNow = leading && !timeout;

				if (timeout)
					$timeout.cancel(timeout);

				timeout = $timeout(later, delay, false);

				if (callNow) {
					if (invokeApply !== false) $rootScope.$applyAsync();
					result = callback.apply(context, args);
				}

				return result;
			};

			debounce.cancel = function () {
				$timeout.cancel(timeout);
				timeout = null;
			};

			return debounce;
		};
	};

	function $throttle($rootScope, $timeout) {

		/**
		 * Service that creates and returns a new throttled version of the passed function, which will trigger its execution only every **delay** milliseconds. This is useful to tear down fast iterative events, like `window.resize`.
		 * @ngdoc factory
		 * @name $throttle
		 * @module qpDebounceThrottle
		 * @param callback {function} The function we want to throttle.
		 * @param delay {number} Number of milliseconds to wait between each input before invoking the function.
		 * @param [leading=false] {boolean} If true, the function will be invoked at the startup. Default: `false`
		 * @param [trailing=true] {boolean} If true, the function will be invoked at end of the operation. Default: `true`
		 * @param [invokeApply=false] {boolean} If true, an an angular digest cycle is triggered each time the function is invoked. Default: `false`
		 * @return A throttled version of the passed function. Any arguments passed to this function will be also passed. The returned function also has a `cancel()` method, which can be used in case you want to reset the current throttle state. This will cause that the next input will trigger the function immediately.
		 */
		/**
		 *
		 *
		 * @param {function} callback 需要控制的函数
		 * @param {number} delay 在调用函数之前，每个输入之间等待的毫秒数。
		 * @param {boolean} leading 如果为真，则在启动时调用该函数。默认值:假
		 * @param {boolean} trailing 如果为真，则在操作结束时调用该函数。默认值:真
		 * @param {boolean} invokeApply 如果为真，则每次调用该函数时都会触发一个angular摘要循环。默认值:假
		 * @returns 
		 */
		return function (callback, delay, leading, trailing, invokeApply) {
			/**
			 * context:执行环境
			 * args：传入callback函数中的参数
			 * timeout：定时器
			 * result ：callback函数执行的返回参数
			 * previous：上一次执行时间
			 */
			var context, args, timeout, result;
			var previous = 0;

			var later = function () {
				//设置上一次执行时间leading为假就为0，否则就取当前浏览器时间
				previous = leading === false ? 0 : (new Date().getTime());
				// 清空定制器
				timeout = null;

				if (invokeApply === true) $rootScope.$applyAsync();
				//执行回调函数并赋值
				result = callback.apply(context, args);
				//timeout被清空后，清空执行环境和参数
				if (!timeout)
					context = args = null;
			};

			var throttled = function () {

				context = this;
				args = arguments;
				//获取当前时间
				var now = (new Date().getTime());
				//设置previous的值如果有配置leading这previous为当前执行时间
				if (!previous && leading === false)
					previous = now;

				//获取到需要间隔的时间秒数
				var remaining = delay - (now - previous);

				//间隔时间小于0或者间隔时间大于函数执行需要等待的时间
				if (remaining <= 0 || remaining > delay) {
					//如果间隔时间到了，还在存在定时器，则会清空定制器
					if (timeout) {
						$timeout.cancel(timeout);
						timeout = null;
					}
					//设置当前时间为上一次执行时间
					previous = now;
					//$applyAsync()来合并处理几乎在相同时间得到的http响应。
					//对于同时发起很多HTTP请求(一般在应用启动阶段)的大型应用，能够大幅提高性能。
					if (invokeApply === true) $rootScope.$applyAsync();

					//执行传入的回调函数,并将函数返回的参数复制返回出去
					result = callback.apply(context, args);

					if (!timeout)
						context = args = null;
				} else if (!timeout && trailing !== false) {
					//在没有定时器
					//并且trailing为假时
					//将定义的后续处理函数传入定制器，将间隔的秒数设置在定时器上
					timeout = $timeout(later, remaining, false);
				};

				return result;
			};

			throttled.cancel = function () {
				//取消定时器 取消后事件不会执行
				$timeout.cancel(timeout);
				//还原上次执行时间
				previous = 0;
				//清空定制器、执行环境、参数
				timeout = context = args = null;
			};

			return throttled;
		};
	};

	function $throttling($rootScope) {
		var group = {};
		/** 
		 * @param {function} callback 需要控制的函数
		 * @param {String} groupId 
		 * @param {boolean} invokeApply 如果为真，则每次调用该函数时都会触发一个angular摘要循环。默认值:假 
		 * @returns 回调函数的返回值
		 */
		return function (callback, groupId, invokeApply) {
			/**
			 * context:执行环境
			 * args:传入callback函数中的参数
			 * result:callback函数执行的返回参数
			 * runing:用来设置运行的状态
			 */
			var context, args, result;
			var throttled = function () {

				if (!group[groupId] || !group[groupId].status) {
					group[groupId] = {
						status: true
					};
					// builderDiv = document.createElement("div");
					// builderDiv.style.width = document.documentElement.clientWidth + "px";
					// builderDiv.style.height = document.documentElement.clientHeight + "px";
					// builderDiv.style.setProperty("cursor", "wait", "important");
					// builderDiv.style.setProperty("id", "builderDiv");
					// builderDiv.style.setProperty("z-index", "1050");
					// builderDiv.style.setProperty("position", "fixed");
					// builderDiv.style.setProperty("top", "0");
					// builderDiv.style.setProperty("right", "0");
					// builderDiv.style.setProperty("bottom", "0");
					// builderDiv.style.setProperty("left", "0");
					// document.body.appendChild(builderDiv);
					document.body.style.setProperty("cursor", "wait", "important");
				} else if (group[groupId].status) {
					return;
				};
				
				context = this;
				args = arguments;
				//对于同时发起很多HTTP请求(一般在应用启动阶段)的大型应用，能够大幅提高性能。
				if (invokeApply === true) $rootScope.$applyAsync();
				//执行传入的回调函数,并将函数返回的参数复制返回出去
				result = callback.apply(context, args);

				return result;
			};
			throttled.runOut = function (groupId, callBack) {
				if (group[groupId]) {
					group[groupId].status = false;
					context = args = null;
					callBack && callBack();
					// builderDiv && builderDiv.remove();
					document.body.style.setProperty("cursor", "default");
				};
			};
			return throttled;
		};
	};

	angular.module('qpDebounceThrottle', [])
		.factory('$debounce', ['$rootScope', '$timeout', $debounce])
		.factory('$throttle', ['$rootScope', '$timeout', $throttle])
		.factory('$throttling', ['$rootScope', $throttling]);
})();